import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, Input, input, OnDestroy, OnInit, signal, TemplateRef, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FirmModel, SPIN_APPEND, SPIN_STYLES, SpinInputModel, UserDetailModel, UserModel, UserTimeZoneModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { TimezoneService } from 'src/app/services/timezone.service';
import { UniqueEmailValidator } from '../../../helpers/unique-email-validator.directive';
import { BillingModel } from '../../../model/billing/billing.model';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { BillingService } from '../../../services/billing.service';
import { FirmService } from '../../../services/firm.service';
import { finalize } from 'rxjs';
import { ImageCropperDialogComponent } from '../image-cropper-dialog/image-cropper-dialog.component';
import { ColorPickerDialogueComponent } from '../color-picker-dialogue/color-picker-dialogue.component';
import { GetLogoModel } from 'src/app/model/firm/firm-logo.model';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { AvatarModule } from 'ngx-avatars';
import { NgSelectModule } from '@ng-select/ng-select';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { FirmInformationComponent } from '../firm-information/firm-information.component';
import { LogoType, RemoveLogoMessage, SaveLogoMessage } from 'src/app/helpers/logo.const';
import { SpinDirective } from 'src/app/directives/spin.directive';
import { DiableAutofillDirective } from 'src/app/helpers/disable-autofill.directive';
import { AclModel } from 'src/app/model/acl.model';
import { AclService } from 'src/app/services/acl.service';


@Component({
  selector: 'app-user-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  providers: [DecimalPipe],
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatExpansionModule, ReactiveFormsModule, FormsModule, 
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    MatCheckboxModule,
    MatRadioModule,
    AvatarModule,
    NgSelectModule,
    ChangePasswordComponent,
    FirmInformationComponent,
    SpinDirective,
    DiableAutofillDirective
  ],
  standalone: true
})
export class UserProfileComponent implements OnInit, OnDestroy {

  @ViewChild('updateTimeZoneTemplate') updateTimeZoneTemplate: TemplateRef<any>;
  @ViewChild('userAcknowledgementDialog') userAcknowledgementDialog: TemplateRef<any>;
  @ViewChild('nameChangeRestrictionDialog') nameChangeRestrictionDialog: TemplateRef<any>;
  @ViewChild('removeFirmLogoTemplate') removeFirmLogoTemplate: TemplateRef<any>;
  @ViewChild('removeProfileImageTemplate') removeProfileImageTemplate: TemplateRef<any>;

  @Input() selectedTabIndex: number = 0;
  
  profilePic: WritableSignal<string> = signal<string>('');
  isProfilePicAvailable: WritableSignal<boolean> = signal<boolean>(false);
  firmLogo: WritableSignal<string> = signal<string>('');
  isFirmLogoAvailable: WritableSignal<boolean> = signal<boolean>(false);
  editUserForm: UntypedFormGroup;
  isUserDetailsEditMode: boolean = false;
  isChangedEmail: boolean = false;

  user: UserModel;
  userDetail: UserDetailModel;
  avatarText: WritableSignal<string> = signal<string>('');
  firm: FirmModel;
  billing: BillingModel;
  selectedTimeZone = new FormControl(null, Validators.required);
  listOfTimeZones: UserTimeZoneModel;
  defaultValue: string = null;
  private destroyRef = inject(DestroyRef);
  public spinnerConfig: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN,false)); 
  public spinnerConfigTimeZone: WritableSignal<SpinInputModel> = signal<SpinInputModel>(new SpinInputModel(SPIN_APPEND.HOST, SPIN_STYLES.BLUR_WITH_SPIN,false)); 
  private spinCount: number = 0; 
  selectedColor: WritableSignal<string> = signal<string>('');
  isChangePassword: WritableSignal<boolean> = signal<boolean>(false);
  profileDetails: WritableSignal<any> = signal<any>({});
  formValueSignal: WritableSignal<any> = signal<any>({});
  isProfileNoChanges = computed(() => {
    const formValue = this.formValueSignal();
    const originalValue = this.profileDetails();
    const normalizedFormValue = { ...formValue };
    const normalizedOriginalValue = { ...originalValue };
    const normalizeValue = (value: any): string => {
      if (value === null || value === undefined || value === "-" || value === 0 || value === "0") {
        return "";
      }
      if (typeof value === 'number') {
        return value.toString();
      }
      if (typeof value === 'string') {
        return value.trim();
      }
      return value;
    };

    Object.keys(normalizedFormValue).forEach(key => {
      normalizedFormValue[key] = normalizeValue(normalizedFormValue[key]);
      normalizedOriginalValue[key] = normalizeValue(normalizedOriginalValue[key]);
    });
    return JSON.stringify(normalizedFormValue) === JSON.stringify(normalizedOriginalValue);
  });
  acl: AclModel;

  constructor(private fb: UntypedFormBuilder,
              private toastr: ToastrService, public apiService: ApiService, public dialog: MatDialog,
              private firmService: FirmService, private billingService: BillingService, private authService: AuthService,
              private timeZoneService: TimezoneService, private aclService: AclService) {
  }

  get memberId() {
    return this.editUserForm.get('memberID');
  }

  get userEmail() {
    return this.editUserForm.get('userEmail');
  }

  ngOnInit() {
    this.getUserProfilePicture();
    this.getFirmLogo();
    localStorage.setItem('profile_email_validation', '1');
    this.getUserTimeZones();
    // login user
    this.authService.subjectUserDetail.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(detail => {
      this.userDetail = detail;
      this.avatarText.set(detail.firstname.charAt(0).toUpperCase() + ' ' + detail.lastname.charAt(0).toUpperCase());
      this.createUserForm();
    });
    this.authService.subjectUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.user = user;
    });
    // firm & billing
    this.firmService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(firm => {
      if (firm) {
        this.firm = firm;
        this.billingService.getBillingDetail().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(billing => {
          if (!billing) {
            return;
          }
          this.billing = billing;
        });
      }
    });
    this.timeZoneService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      if (response) {
        this.defaultValue = response.uniqueId;
        this.selectedTimeZone.setValue(response.uniqueId);
      }
    });
    this.aclService.subject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        this.acl = res;
      }
    });
    this.initialForm();
  }

  updateProfile() {
    if (this.editUserForm.valid) {
      const editUserEmail = this.editUserForm.getRawValue() ? this.editUserForm.getRawValue()?.userEmail.toLowerCase() : '';
      this.isChangedEmail = this.userDetail.emailAddress.toLowerCase() !== editUserEmail;
      const editedFirstName = this.editUserForm.getRawValue() ? this.editUserForm.getRawValue()?.firstName.toLowerCase() : '';
      const editedLastName = this.editUserForm.getRawValue() ? this.editUserForm.getRawValue()?.lastName.toLowerCase() : '';
      if (this.userDetail.firstname.toLowerCase() !== editedFirstName || this.userDetail.lastname.toLowerCase() !== editedLastName) {
        this.dialog.open(this.nameChangeRestrictionDialog, {disableClose: true}).afterClosed().subscribe(() => {
          this.editUserForm.controls['firstName'].setValue(this.userDetail.firstname);
          this.editUserForm.controls['lastName'].setValue(this.userDetail.lastname);
        });
        return;
      }
      const data = {
        'firstName': this.editUserForm.value.firstName,
        'lastName': this.editUserForm.value.lastName,
        'cellPhoneNumber': this.editUserForm.value.cellPhone,
        'businessPhoneNumber': this.editUserForm.value.businessPhone,
        'memberLicenseNumber': this.editUserForm.value.licenseNumber,
        'emailAddress': editUserEmail,
        'memberId': this.editUserForm.value.memberID,
        'hourlyRate': this.editUserForm.value.hourlyrate,
        'designationName': this.editUserForm.value.title ? this.editUserForm.value.title : ''
      };
      this.updateSpinnerConfig(true);
      this.authService.updateUserProfile(data).pipe(finalize(() => this.updateSpinnerConfig(false))).subscribe({
        next: res => {
          if (res.status === 200) {
            this.isUserDetailsEditMode = false;
            if (this.isChangedEmail) {
              this.dialog.open(this.userAcknowledgementDialog, {disableClose: true}).afterClosed().subscribe(value => {
                this.toastr.success('Signed out successfully');
                this.authService.logout();
              });
            } else {
              this.authService.refreshUserProfile().subscribe();
              this.profileDetails.set(this.editUserForm.getRawValue());
            }
          }
          res.messages.forEach(msg => {
            this.toastr.success(msg);
          });
          res.errors.forEach(err => {
            this.toastr.error(err);
          });
        }, error: error => {
          this.toastr.error(error.error);
        }
      });
    }
  }

  openEditForm() {
    this.isUserDetailsEditMode = true;
    this.editUserForm.patchValue({
      memberID: this.userDetail.memberid,
      firstName: this.userDetail.firstname,
      lastName: this.userDetail.lastname,
      userEmail: this.userDetail.emailAddress,
      businessPhone: this.userDetail.businessphoneNumber,
      cellPhone: this.userDetail.cellphonenumber === 'null' ? '' : this.userDetail.cellphonenumber,
      hourlyrate: this.userDetail.hourlyrate,
      licenseNumber: this.userDetail.licenseno,
      title: this.userDetail.title
    });
    this.formValueSignal.set(this.editUserForm.getRawValue());
    if (this.userDetail.userroleId === 11) {
      this.editUserForm.controls['userEmail'].disable();
    }
    this.editUserForm.get('firstName')?.setValidators([Validators.required]);
    this.editUserForm.get('lastName')?.setValidators([Validators.required]);
    this.editUserForm.get('title')?.setValidators([Validators.required]);
    this.editUserForm.get('businessPhone')?.setValidators([Validators.minLength(14), Validators.maxLength(14)]);
    this.editUserForm.get('cellPhone')?.setValidators([Validators.minLength(14), Validators.maxLength(14)]);
    this.editUserForm.get('userEmail')?.setValidators([Validators.required]);
    this.editUserForm.get('userEmail')?.setAsyncValidators([UniqueEmailValidator(this.firmService, this.authService, this.toastr)]);
    // Apply changes, these are not needed, just keep them here in case we faced an issue here
    // this.editUserForm.get('firstName')?.updateValueAndValidity();
  }

  public handlePhone(event, type) {
    const onlyNums = event.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length < 10) {
      if (type == 'cell') {
        this.editUserForm.controls['cellPhone'].setValue(onlyNums);
      } else if (type == 'business') {
        this.editUserForm.controls['businessPhone'].setValue(onlyNums);
      } else {
        return;
      }
    } else if (onlyNums.length === 10) {
      const number = onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      if (type == 'cell') {
        this.editUserForm.controls['cellPhone'].setValue(number);
      } else if (type == 'business') {
        this.editUserForm.controls['businessPhone'].setValue(number);
      } else {
        return;
      }
    }
  };

  public onlyChars(event, type) {
    const noSpecialCHars = event.target.value.replace(/[^a-zA-Z ]/g, '');
    if (type === 'userfn') {
      this.editUserForm.controls['firstName'].setValue(noSpecialCHars);
    } else if (type === 'userln') {
      this.editUserForm.controls['lastName'].setValue(noSpecialCHars);
    } else {
      return;
    }
  }

  alphaNumeric(event) {
    const alphaNumeric = event.target.value.replace(/[^a-zA-Z0-9]/g, '');
    this.editUserForm.controls['memberID'].setValue(alphaNumeric);
  }

  validateMemberId() {
    setTimeout(() => {
      if (this.memberId.invalid) {
        if (this.memberId.errors.uniqueId) {
          this.toastr.error('Member Id already exists');
          return;
        }
      }
    }, 1000);
  }

  checkForDecimal(event) {
    const input = event.target.value;
    const index = input.indexOf('.');
    if (index <= 0 && (input.length > 14 && index != 14)) {
      event.target.value = +(event.target.value.slice(0, 14));
      this.editUserForm.value.hourlyrate = +event.target.value;
    }
  }

  getUserTimeZones() {
    this.timeZoneService.getTimezoneList().subscribe({
      next: response => {
        if (response.status === 200) {
          this.listOfTimeZones = response.data;
        }
      },
      error: err => {
        this.toastr.error(err.error);
      }
    });
  }

  public confirmSelectedTimeZone() {
    this.dialog.open(this.updateTimeZoneTemplate, {disableClose: true}).afterClosed().subscribe(value => {
      if (value) {
        this.updateUserTimeZone();
      } else {
        this.selectedTimeZone.setValue(this.defaultValue);
      }
    });
  }

  ngOnDestroy(): void {
    localStorage.removeItem('profile_email_validation');
  }

  initialForm() {
    this.profileDetails.set({
      memberID: this.userDetail.memberid && this.userDetail.memberid !== 'null' ? this.userDetail.memberid : '-',
      firstName: this.userDetail.firstname,
      lastName: this.userDetail.lastname,
      userEmail: this.userDetail.emailAddress,
      businessPhone: this.userDetail.businessphoneNumber ? this.userDetail.businessphoneNumber : '-',
      cellPhone: this.userDetail.cellphonenumber ? this.userDetail.cellphonenumber : '-',
      hourlyrate: this.userDetail.hourlyrate ? this.userDetail.hourlyrate : '-',
      licenseNumber: this.userDetail.licenseno ? this.userDetail.licenseno : '-',
      title: this.userDetail.title ? this.userDetail.title : '-'
    });    
  }

  private createUserForm() {
    this.editUserForm = this.fb.group({
      'memberID': [''],
      'firstName': [''],
      'lastName': [''],
      'userEmail': this.fb.control('', {updateOn: 'blur'}),
      'businessPhone': [''],
      'cellPhone': [''],
      'hourlyrate': [''],
      'licenseNumber': [''],
      'title': ['']
    });
    this.editUserForm.valueChanges.subscribe(() => {
      this.formValueSignal.set(this.editUserForm.getRawValue());
    });
    this.openEditForm();
  }

  private updateUserTimeZone() {
    this.spinnerConfigTimeZone.set({ ...this.spinnerConfigTimeZone(), isSpinShown: true });
    this.timeZoneService.updateTimeZone(this.selectedTimeZone.value).pipe(finalize(() => this.spinnerConfigTimeZone.set({ ...this.spinnerConfigTimeZone(), isSpinShown: false }))).subscribe({
      next: response => {
        if (response.status === 200) {
          this.timeZoneService.getUserTimeZone().subscribe();
          this.toastr.success('Time zone updated successfully');
        }
      },
      error: err => {
        this.toastr.error(err.error);
      }
    });
  }

  private updateSpinnerConfig(value: boolean) { 
      value ? this.spinCount++ : (this.spinCount = Math.max(0, this.spinCount - 1)); 
      const spinner = this.spinnerConfig(); 
      this.spinnerConfig.set({ 
       ...spinner, 
         isSpinShown: this.spinCount > 0  
       }); 
 
     }

     fileChangeEventProfile(event: any, fileInput: HTMLInputElement): void {
      const dialogRef = this.dialog.open(ImageCropperDialogComponent, {
        width: '500px',
        data: { event, heading: 'Update Profile Image', subHeading: 'Make adjustments for better visibility and save your profile image' }
      });
    
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.uploadProfilePictureAndFirmLogo(result.croppedImage, LogoType.PROFILE_PICTURE);
          // this.downloadDataUrlAsFile(result.croppedImage, 'profile_picture.png');
        }
        fileInput.value = '';
      });
    }

    fileChangeEventFirm(event: any, fileInput: HTMLInputElement): void {
      const dialogRef = this.dialog.open(ImageCropperDialogComponent, {
        width: '500px',

        data: { event, heading: 'Update Firm Logo', subHeading: 'Make adjustments for better visibility and save the logo' }
      });
    
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.uploadProfilePictureAndFirmLogo(result.croppedImage, LogoType.FIRM_LOGO);
        }
        fileInput.value = '';
      });
    }

    removeImageDialogue(type: LogoType) {
      if (type === LogoType.PROFILE_PICTURE) {
        this.dialog.open(this.removeProfileImageTemplate, {disableClose: true}).afterClosed().subscribe(value => {
          if (value) {
            this.removeProfileImage(type);
          }
        });
      } else if (type === LogoType.FIRM_LOGO) {
        this.dialog.open(this.removeFirmLogoTemplate, {disableClose: true}).afterClosed().subscribe(value => {
          if (value) {
            this.removeProfileImage(type);
          }
        });
      }
    }

    removeProfileImage(type: LogoType){
      this.firmService.removeLogo(type).subscribe({
        next: (res) => {
          if (res.status === 200) {
            if (type === LogoType.PROFILE_PICTURE) {
              this.profilePic.set('');
              this.isProfilePicAvailable.set(false);
              this.getUserProfilePicture();
              this.toastr.success(RemoveLogoMessage.PROFILE_PICTURE);
              this.firmService.profilePicChanged$.next(true);
            } else if (type === LogoType.FIRM_LOGO) {
              this.firmLogo.set('');
              this.isFirmLogoAvailable.set(false);
              this.getFirmLogo();
              this.toastr.success(RemoveLogoMessage.FIRM_LOGO);
            }
          }
        },
        error: err => {
          this.toastr.error('Failed to remove profile picture');
        }
      });
    }

    openColorDialog(): void {
      const dialogRef = this.dialog.open(ColorPickerDialogueComponent, {
        width: '500px',
        data: { selectedColor: this.selectedColor(), avatarName : this.avatarText() }
      });
    
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.selectedColor.set(result);
          this.uploadProfileBgColor();
        }
      });
    }


    getUserProfilePicture() : void {
      this.firmService.getLogo(LogoType.PROFILE_PICTURE).subscribe({
        next: (res : GetLogoModel) => {
          this.profilePic.set(res?.pictureUrl);
          this.selectedColor.set(res?.bgColorCode);
          this.isProfilePicAvailable.set(!!res?.pictureUrl);
        },
        error: err => {
          this.profilePic.set('');
          this.isProfilePicAvailable.set(false);
          this.toastr.error('Failed to load profile picture');
        }
      });
    }

    getFirmLogo() : void {
      this.firmService.getLogo(LogoType.FIRM_LOGO).subscribe({
        next: (res : GetLogoModel) => {
          this.firmLogo.set(res?.pictureUrl);
          this.isFirmLogoAvailable.set(!!res?.pictureUrl);
        },
        error: err => {
          this.firmLogo.set('');
          this.isFirmLogoAvailable.set(false);
          this.toastr.error('Failed to load firm logo');
        }
      });
    }

    uploadProfilePictureAndFirmLogo(croppedImage: string, type: LogoType): void {
      const formData = new FormData();
      formData.append('file', this.dataURLtoFile(croppedImage, 'profile_picture.png'));
      formData.append('type', type);
      formData.append('colorCode', '');
      this.updateSpinnerConfig(true)
      this.firmService.saveLogo(formData)
      .pipe(finalize(() => this.updateSpinnerConfig(false)))
      .subscribe({
        next: (res) => {
          if(res.status !== 200) {
            res.errors.forEach((err : string) => {
              this.toastr.error(err);
            });
            return;
          }
          if(type === LogoType.PROFILE_PICTURE) {
            this.getUserProfilePicture();
            this.toastr.success(SaveLogoMessage.PROFILE_PICTURE);
            this.firmService.profilePicChanged$.next(true);
          } else if(type === LogoType.FIRM_LOGO) {
            this.getFirmLogo();
            this.toastr.success(SaveLogoMessage.FIRM_LOGO);
          }
        },
        error: err => {
          this.toastr.error('Failed to upload profile picture');
        }
      });
    }

    uploadProfileBgColor(): void {
      const formData = new FormData();
      formData.append('file',  new File([], 'profile_background_color.png'));
      formData.append('type', LogoType.PROFILE_PICTURE);
      formData.append('colorCode',  this.selectedColor());
      this.firmService.saveLogo(formData).subscribe({
        next: (res) => {
          if(res.status !== 200) {
            res.errors.forEach((err : string) => {
              this.toastr.error(err);
            });
            return;
          }
          this.getUserProfilePicture();
          this.toastr.success(SaveLogoMessage.PROFILE_BGCOLOR);
          this.firmService.profilePicChanged$.next(true);
        },
        error: err => {
          this.toastr.error('Failed to upload profile background color');
        }
      });
    }


    private dataURLtoFile(dataurl: string, filename: string): File {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    }

    // Add this method for testing dataURLtoFile by downloading the file
    downloadDataUrlAsFile(dataurl: string, filename: string): void {
      const file = this.dataURLtoFile(dataurl, filename);
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }

  onTimeZoneChange(selected: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
  }

}
